import React from "react";
import { withStyles } from "@material-ui/styles";
import { I18n } from "@aws-amplify/core";
import Slider from "@material-ui/core/Slider";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import FlashOn from "@material-ui/icons/FlashOn";
import FlashOff from "@material-ui/icons/FlashOff";
import ZoomIn from "@material-ui/icons/ZoomIn";
import CenterFocusStrong from "@material-ui/icons/CenterFocusStrong";
import CenterFocusWeak from "@material-ui/icons/CenterFocusWeak";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

/**
 * Scanner de code QR par caméra (téléphone / tablette / webcam).
 *
 * Le décodage se fait dans l'application (html5-qrcode → ZXing, ou l'API
 * navigateur `BarcodeDetector` quand elle est disponible). Aucun service
 * externe n'est impliqué.
 *
 * Les étiquettes Zebra produisent des codes QR denses et petits (modules de
 * ~0,5 mm à 203 dpi), imprimés sur média thermique peu contrasté. Le zoom et la
 * torche sont donc exposés quand la caméra les supporte : ce sont les deux
 * leviers qui font la différence entre un code lisible et un code illisible.
 */

// Fréquence de décodage. 10 images/s suffit et reste raisonnable pour la
// batterie et le CPU des téléphones d'entrepôt.
const SCAN_FPS = 10;

// Un même code reste dans le champ de la caméra pendant plusieurs images.
// On ignore les répétitions dans cette fenêtre pour ne pas déclencher
// plusieurs fois la même recherche (ou répéter le même message d'erreur).
const REPEAT_DECODE_WINDOW_MS = 1500;

// Le viseur occupe cette fraction du plus petit côté de l'aperçu vidéo.
const QRBOX_RATIO = 0.75;
const MIN_QRBOX_SIZE = 120;

// `start()` se résout avant que l'événement `playing` n'ait déclenché la mise en
// place du viseur : il faut attendre l'apparition du canvas de scan, puis les
// métadonnées du flux, avant de pouvoir juger la géométrie.
const SCAN_REGION_TIMEOUT_MS = 3000;
const METADATA_TIMEOUT_MS = 3000;
const POLL_INTERVAL_MS = 50;
const LAYOUT_SETTLE_TIMEOUT_MS = 100;

// Tolérance en pixels sur la comparaison de géométrie (arrondis de layout).
const GEOMETRY_TOLERANCE_PX = 2;

// Périodicité des traces de diagnostic quand aucune image ne se décode.
const DECODE_DIAGNOSTIC_INTERVAL_MS = 5000;

// Mise au point. Sur Chrome Android, `focusMode` et `focusDistance` sont
// pilotables par contraintes; ce n'est pas le cas sur Chrome desktop ni sur
// Safari iOS. Tout ce qui touche au focus est donc conditionné à la détection
// des capacités réelles de la piste vidéo.
//
// Contexte: plusieurs téléphones Android, les Samsung en particulier, exposent
// plusieurs modules arrière et leur autofocus continu peut chercher longtemps
// avant de se stabiliser sur une étiquette tenue de près (bug Chromium 40276436).
// Deux leviers sont utilisés ici: demander explicitement le focus continu, et
// offrir une mise au point manuelle quand l'autofocus n'arrive pas à se fixer.
const FOCUS_CONTINUOUS = "continuous";
const FOCUS_MANUAL = "manual";

// Nombre de crans du curseur de mise au point manuelle quand l'appareil ne
// publie pas de pas utilisable pour `focusDistance`.
const FOCUS_SLIDER_STEPS = 100;

// Délai d'apaisement avant de redémarrer après un changement de largeur.
// Une rotation déclenche une rafale de redimensionnements, il ne faut réagir
// qu'une fois la nouvelle mise en page stabilisée.
const RESIZE_SETTLE_MS = 300;

let scannerInstanceCount = 0;

// Hauteur minimale de l'aperçu quand la fenêtre est trop basse pour l'afficher
// en entier. Reste supérieure au viseur sur un téléphone en portrait, pour que
// le cadre de visée demeure lisible.
const MIN_VISIBLE_PREVIEW_HEIGHT = "180px";

const styles = {
  root: {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  // html5-qrcode mesure `clientWidth` du conteneur au démarrage et y insère la
  // vidéo : il doit rester affiché et dimensionné pendant toute la phase de
  // démarrage, sinon le viseur est calculé sur des dimensions nulles.
  //
  // Sur un téléphone en portrait, le flux est plus haut que large et l'aperçu
  // déborde de l'écran. Ce cadre peut donc rétrécir: il rogne la vidéo à parts
  // égales en haut et en bas, sans toucher à ses dimensions. Le viseur, centré,
  // reste visible, et la correspondance entre la zone affichée et la zone
  // analysée (calculée sur les dimensions de l'élément vidéo) est préservée.
  viewfinderWrapper: {
    position: "relative",
    width: "100%",
    flex: "1 1 auto",
    minHeight: MIN_VISIBLE_PREVIEW_HEIGHT,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderRadius: "2px",
    overflow: "hidden",
    backgroundColor: "#000000",
  },
  // Aucune règle sur `& video`: la librairie fixe elle-même la largeur de la
  // vidéo en pixels et laisse la hauteur suivre le ratio du flux. Forcer l'une
  // ou l'autre en CSS casse le calcul de correspondance entre le viseur affiché
  // et la zone découpée dans l'image source.
  viewfinder: {
    width: "100%",
    minHeight: "240px",
    flexShrink: 0,
  },
  startingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    color: "#ffffff",
    fontSize: "14px",
  },
  status: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minHeight: "240px",
    padding: "0 16px",
    textAlign: "center",
    fontSize: "14px",
  },
  hint: {
    marginTop: "8px",
    fontSize: "12px",
    color: "#00000099",
    textAlign: "center",
  },
  controls: {
    width: "100%",
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  zoomSlider: {
    flex: 1,
    marginRight: "8px",
  },
  controlLabel: {
    fontSize: "12px",
    color: "#00000099",
    whiteSpace: "nowrap",
  },
  errorMsg: {
    color: "#eb3c39",
    fontSize: "14px",
    textAlign: "center",
    padding: "0 16px",
  },
};

const describeCameraError = (error) => {
  const name = error && error.name ? error.name : "";
  const text = `${name} ${error && error.message ? error.message : error}`;

  if (name === "NotAllowedError" || name === "SecurityError" || /permission|denied|NotAllowed/i.test(text)) {
    return I18n.get("qrScanPermissionDenied");
  }
  if (name === "NotFoundError" || name === "OverconstrainedError" || /no camera|NotFound|devices/i.test(text)) {
    return I18n.get("qrScanNoCamera");
  }
  if (name === "NotReadableError" || /in use|NotReadable|TrackStart/i.test(text)) {
    return I18n.get("qrScanCameraBusy");
  }
  return I18n.get("qrScanCameraError");
};

/**
 * Referme le flux sans propager d'erreur. `stop()` rejette si le scanner n'a
 * jamais démarré, ce qui est sans conséquence ici.
 */
const stopQuietly = (scanner) =>
  Promise.resolve()
    .then(() => scanner.stop())
    .then(() => scanner.clear())
    .catch(() => {});

/**
 * Applique une contrainte de mise au point sur la piste vidéo en cours.
 *
 * `advanced` est la même forme que celle utilisée par html5-qrcode pour le zoom
 * et la torche: le navigateur l'applique en complément des contraintes de base.
 * Un `applyConstraints` de premier niveau remettrait au contraire les propriétés
 * absentes à leur valeur par défaut, ce qui ferait perdre la résolution demandée
 * au démarrage.
 */
const applyFocusConstraint = (scanner, constraint) => {
  try {
    return scanner.applyVideoConstraints({ advanced: [constraint] }).catch((err) => {
      console.warn("Failed to apply focus constraint:", constraint, err);
    });
  } catch (err) {
    console.warn("Failed to apply focus constraint:", constraint, err);
    return Promise.resolve();
  }
};

const computeQrBox = (viewfinderWidth, viewfinderHeight) => {
  const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
  const size = Math.max(MIN_QRBOX_SIZE, Math.floor(minEdge * QRBOX_RATIO));
  return { width: size, height: size };
};

const CameraScanner = ({ classes, onDecoded, onError }) => {
  const [elementId] = React.useState(() => `camera-scanner-${++scannerInstanceCount}`);
  const [isStarting, setIsStarting] = React.useState(true);
  const [startupError, setStartupError] = React.useState("");
  const [zoom, setZoom] = React.useState(null);
  const [zoomRange, setZoomRange] = React.useState(null);
  const [torchSupported, setTorchSupported] = React.useState(false);
  const [torchOn, setTorchOn] = React.useState(false);

  // Plage de `focusDistance` publiée par l'appareil, uniquement si la mise au
  // point manuelle est réellement disponible. `null` = contrôle non affiché.
  const [focusRange, setFocusRange] = React.useState(null);
  const [focusDistance, setFocusDistance] = React.useState(null);
  const [manualFocus, setManualFocus] = React.useState(false);

  // Incrémenté pour forcer un redémarrage complet de la caméra.
  const [restartToken, setRestartToken] = React.useState(0);

  // Hauteur imposée à l'aperçu lors d'un redémarrage, calculée sur le ratio réel
  // du flux. Injectée en CSS pour que la librairie mesure une géométrie correcte
  // dès la première image, sans dépendre de l'arrivée des métadonnées.
  const [forcedVideoHeight, setForcedVideoHeight] = React.useState(null);

  const scannerRef = React.useRef(null);
  const lastDecodeRef = React.useRef({ text: null, at: 0 });
  const failureStatsRef = React.useRef({ count: 0, lastLoggedAt: 0 });

  // Capacités de mise au point relevées au démarrage, reprises dans les traces
  // de diagnostic pour qualifier un échec de lecture depuis le terrain.
  const focusDiagnosticRef = React.useRef(null);

  // `onDecoded` est souvent une closure recréée à chaque rendu. On la garde
  // dans une ref pour ne pas redémarrer la caméra à chaque rendu du parent.
  const onDecodedRef = React.useRef(onDecoded);
  const onErrorRef = React.useRef(onError);
  React.useEffect(() => {
    onDecodedRef.current = onDecoded;
    onErrorRef.current = onError;
  }, [onDecoded, onError]);

  React.useEffect(() => {
    // Drapeau local à cette exécution de l'effet, et non une ref partagée: un
    // redémarrage enchaîne un nettoyage et un nouveau démarrage, et une ref
    // commune permettrait au démarrage abandonné de reprendre son cours en
    // croyant être encore valide, puis d'agir sur un scanner déjà fermé.
    let cancelled = false;

    // Un redémarrage repart d'un flux neuf: torche éteinte et mise au point
    // continue. L'interface doit repartir du même état.
    setIsStarting(true);
    setTorchOn(false);
    setManualFocus(false);

    const reportError = (message) => {
      if (cancelled) return;
      setStartupError(message);
      if (onErrorRef.current) onErrorRef.current(message);
    };

    if (!window.isSecureContext) {
      setIsStarting(false);
      reportError(I18n.get("qrScanInsecureContext"));
      return undefined;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setIsStarting(false);
      reportError(I18n.get("qrScanUnsupportedBrowser"));
      return undefined;
    }

    const getElements = () => {
      const container = document.getElementById(elementId);
      if (!container) return {};
      return {
        container,
        video: container.querySelector("video"),
        canvas: container.querySelector("canvas"),
      };
    };

    const handleDecoded = (decodedText) => {
      failureStatsRef.current = { count: 0, lastLoggedAt: 0 };

      const now = Date.now();
      const last = lastDecodeRef.current;
      if (last.text === decodedText && now - last.at < REPEAT_DECODE_WINDOW_MS) {
        return;
      }
      lastDecodeRef.current = { text: decodedText, at: now };
      if (onDecodedRef.current) onDecodedRef.current(decodedText);
    };

    const handleScanFailure = (errorMessage) => {
      const stats = failureStatsRef.current;
      stats.count += 1;

      const now = Date.now();
      if (stats.lastLoggedAt && now - stats.lastLoggedAt < DECODE_DIAGNOSTIC_INTERVAL_MS) {
        return;
      }
      stats.lastLoggedAt = now;

      const { video, canvas } = getElements();
      console.debug(
        `[CameraScanner] ${stats.count} image(s) analysée(s) sans décodage.`,
        {
          lastError: errorMessage,
          source: video ? `${video.videoWidth}x${video.videoHeight}` : "n/a",
          preview: video ? `${video.clientWidth}x${video.clientHeight}` : "n/a",
          scanRegion: canvas ? `${canvas.width}x${canvas.height}` : "n/a",
          focus: focusDiagnosticRef.current || "n/a",
        }
      );
    };

    const scanConfig = {
      fps: SCAN_FPS,
      qrbox: computeQrBox,
      disableFlip: true,
    };

    const readCameraCapabilities = (scanner) => {
      try {
        const capabilities = scanner.getRunningTrackCameraCapabilities();

        const zoomFeature = capabilities.zoomFeature();
        if (zoomFeature.isSupported()) {
          setZoomRange({ min: zoomFeature.min(), max: zoomFeature.max(), step: zoomFeature.step() || 0.1 });
          setZoom(zoomFeature.value());
        } else {
          setZoomRange(null);
        }

        setTorchSupported(capabilities.torchFeature().isSupported());
      } catch (err) {
        console.warn("Camera capabilities unavailable:", err);
      }

      readFocusCapabilities(scanner);
    };

    /**
     * `CameraCapabilities` de html5-qrcode n'expose que le zoom et la torche:
     * la mise au point se lit donc directement sur les capacités brutes de la
     * piste vidéo.
     */
    const readFocusCapabilities = (scanner) => {
      let trackCapabilities = {};
      let trackSettings = {};
      try {
        trackCapabilities = scanner.getRunningTrackCapabilities() || {};
        trackSettings = scanner.getRunningTrackSettings() || {};
      } catch (err) {
        console.warn("Track capabilities unavailable:", err);
        return;
      }

      const supportedModes = trackCapabilities.focusMode || [];
      const distance = trackCapabilities.focusDistance;

      focusDiagnosticRef.current = {
        focusMode: supportedModes.length ? supportedModes.join("/") : "non pilotable",
        focusDistance: distance ? `${distance.min}..${distance.max}` : "non pilotable",
        activeFocusMode: trackSettings.focusMode || "inconnu",
        resolution: `${trackSettings.width || "?"}x${trackSettings.height || "?"}`,
        deviceId: trackSettings.deviceId || "?",
      };
      console.info("[CameraScanner] Capacités de mise au point", focusDiagnosticRef.current);

      // L'autofocus continu est demandé explicitement: sur plusieurs appareils
      // Android il n'est pas actif par défaut sur le flux getUserMedia.
      if (supportedModes.includes(FOCUS_CONTINUOUS)) {
        applyFocusConstraint(scanner, { focusMode: FOCUS_CONTINUOUS });
      }

      const canFocusManually =
        supportedModes.includes(FOCUS_MANUAL) &&
        distance &&
        typeof distance.min === "number" &&
        typeof distance.max === "number" &&
        distance.max > distance.min;

      if (!canFocusManually) {
        setFocusRange(null);
        return;
      }

      setFocusRange({
        min: distance.min,
        max: distance.max,
        step: distance.step || (distance.max - distance.min) / FOCUS_SLIDER_STEPS,
      });
      // On part de la position courante de l'objectif: l'unité de
      // `focusDistance` varie d'un appareil à l'autre, il n'y a donc rien à
      // deviner sur la valeur qui correspond à une étiquette tenue de près.
      setFocusDistance(
        typeof trackSettings.focusDistance === "number" ? trackSettings.focusDistance : distance.min
      );
    };

    /**
     * Démarre la caméra en essayant successivement:
     *  1. la caméra arrière en haute résolution (plus de pixels = codes denses
     *     décodés de plus loin), avec demande d'autofocus continu,
     *  2. la caméra arrière sans contrainte de résolution,
     *  3. un périphérique choisi explicitement dans la liste.
     *
     * `focusMode` est placé dès la première tentative parce qu'il influence le
     * module physique retenu par le navigateur: les téléphones qui exposent
     * plusieurs caméras arrière peuvent en présenter une sans autofocus
     * exploitable, et c'est le seul moment où ce choix peut encore être orienté.
     */
    const startScanner = async (scanner) => {
      const attempts = [
        {
          config: {
            ...scanConfig,
            videoConstraints: {
              facingMode: { ideal: "environment" },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              focusMode: { ideal: FOCUS_CONTINUOUS },
            },
          },
          camera: { facingMode: "environment" },
        },
        { config: scanConfig, camera: { facingMode: "environment" } },
      ];

      let lastError = null;

      for (const attempt of attempts) {
        if (cancelled) return;
        try {
          await scanner.start(attempt.camera, attempt.config, handleDecoded, handleScanFailure);
          return;
        } catch (err) {
          lastError = err;
          console.warn("Camera start attempt failed:", err);
        }
      }

      // Dernier recours : sélection explicite d'un périphérique. Prendre le
      // dernier de la liste à l'aveugle peut tomber sur un module sans
      // autofocus exploitable, d'où la préférence donnée à un libellé de caméra
      // arrière. La liste complète est tracée pour pouvoir diagnostiquer les
      // appareils où ce choix reste mauvais.
      try {
        if (cancelled) return;
        const cameras = await Html5Qrcode.getCameras();
        if (cameras && cameras.length > 0) {
          console.info(
            "[CameraScanner] Caméras disponibles",
            cameras.map((camera) => camera.label)
          );
          const backCamera = cameras.find((camera) => /back|rear|arrière|environment/i.test(camera.label || ""));
          const fallbackCamera = backCamera || cameras[cameras.length - 1];
          await scanner.start(fallbackCamera.id, scanConfig, handleDecoded, handleScanFailure);
          return;
        }
      } catch (err) {
        lastError = err;
        console.warn("Camera fallback failed:", err);
      }

      throw lastError || new Error("NotFoundError");
    };

    /**
     * Attend qu'une condition devienne vraie, sans dépendre de requestAnimationFrame
     * (qui ne se déclenche pas dans un onglet en arrière-plan).
     * @returns {Promise<boolean>} false en cas d'expiration ou de démontage
     */
    const waitUntil = (predicate, timeoutMs) =>
      new Promise((resolve) => {
        const deadline = Date.now() + timeoutMs;
        const tick = () => {
          if (cancelled) return resolve(false);
          if (predicate()) return resolve(true);
          if (Date.now() >= deadline) return resolve(false);
          return setTimeout(tick, POLL_INTERVAL_MS);
        };
        tick();
      });

    /** Laisse le navigateur appliquer le ratio intrinsèque du flux au layout. */
    const waitForLayout = () =>
      new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        requestAnimationFrame(() => requestAnimationFrame(finish));
        setTimeout(finish, LAYOUT_SETTLE_TIMEOUT_MS);
      });

    /**
     * Attend que la librairie ait posé sa zone de scan (donc figé sa géométrie),
     * puis que les dimensions réelles du flux soient connues et appliquées.
     */
    const waitForVideoGeometry = async () => {
      await waitUntil(() => Boolean(getElements().canvas), SCAN_REGION_TIMEOUT_MS);
      await waitUntil(() => {
        const { video } = getElements();
        return Boolean(video) && video.readyState >= 1 && video.videoHeight > 0;
      }, METADATA_TIMEOUT_MS);
      await waitForLayout();
    };

    /**
     * La zone de scan a-t-elle été calculée sur des dimensions obsolètes ?
     * La librairie dimensionne son canvas d'après le viseur retenu au démarrage.
     * Si ce canvas ne correspond pas à ce que `computeQrBox` produirait avec les
     * dimensions actuelles de l'aperçu, la mesure était prématurée.
     */
    const isViewfinderGeometryStale = () => {
      const { video, canvas } = getElements();
      if (!video || !canvas) return false;
      if (!video.clientWidth || !video.clientHeight) return false;

      const expected = computeQrBox(video.clientWidth, video.clientHeight);
      return (
        Math.abs(canvas.width - expected.width) > GEOMETRY_TOLERANCE_PX ||
        Math.abs(canvas.height - expected.height) > GEOMETRY_TOLERANCE_PX
      );
    };

    // Le constructeur de html5-qrcode lève si le conteneur est absent du DOM.
    // C'est le cas quand une erreur de démarrage a remplacé l'aperçu par son
    // message: inutile de retenter, et surtout pas depuis un redémarrage.
    if (!document.getElementById(elementId)) {
      return undefined;
    }

    const scanner = new Html5Qrcode(elementId, {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      // Décodeur natif du navigateur quand il existe: nettement plus robuste
      // sur les petits codes denses que le décodage logiciel. Absent de Firefox,
      // qui retombe alors sur ZXing seul.
      useBarCodeDetectorIfSupported: true,
      verbose: false,
    });
    scannerRef.current = scanner;

    /**
     * Hauteur d'aperçu correspondant au ratio réel du flux. En l'imposant en CSS,
     * la vidéo a sa taille définitive dès son insertion : la mesure faite sur
     * `playing` est alors exacte quel que soit le moment d'arrivée des
     * métadonnées. Le ratio étant celui du flux, la zone découpée reste carrée et
     * le code QR n'est pas déformé.
     */
    const computeForcedVideoHeight = () => {
      const { video } = getElements();
      if (!video || !video.videoWidth || !video.videoHeight || !video.clientWidth) {
        return null;
      }
      const height = Math.round((video.clientWidth * video.videoHeight) / video.videoWidth);
      return height > 0 && height < 4000 ? height : null;
    };

    const start = async () => {
      await startScanner(scanner);
      if (cancelled) return;

      await waitForVideoGeometry();
      if (cancelled) return;

      if (!isViewfinderGeometryStale()) return;

      const height = computeForcedVideoHeight();
      console.info(
        "[CameraScanner] Viseur mesuré avant les métadonnées du flux (Firefox). " +
          `Redémarrage avec une hauteur d'aperçu imposée de ${height}px.`
      );

      if (height) {
        setForcedVideoHeight(height);
        // Laisse React insérer la règle CSS avant de recréer l'élément vidéo.
        await waitForLayout();
        if (cancelled) return;
      }

      await scanner.stop();
      if (cancelled) return;
      await startScanner(scanner);
    };

    start()
      .then(() => {
        // Démonté ou redémarré pendant le démarrage: le cleanup a déjà tenté un
        // stop() alors que le scanner n'était pas encore actif, il faut
        // refermer ici, sinon la caméra reste allumée sans personne pour la lire.
        if (cancelled) {
          stopQuietly(scanner);
          return;
        }
        setIsStarting(false);
        readCameraCapabilities(scanner);
      })
      .catch((err) => {
        console.error("Failed to start QR camera scanner:", err);
        if (cancelled) {
          stopQuietly(scanner);
          return;
        }
        setIsStarting(false);
        reportError(describeCameraError(err));
      });

    return () => {
      cancelled = true;
      scannerRef.current = null;
      stopQuietly(scanner);
    };
  }, [elementId, restartToken]);

  /**
   * html5-qrcode fige la géométrie de sa zone de scan au démarrage: la taille du
   * canvas de découpe et sa correspondance avec l'image source sont calculées
   * une seule fois. Après une rotation de l'appareil, l'utilisateur centre donc
   * le code dans un cadre qui ne correspond plus à la zone réellement analysée,
   * et plus rien ne se décode — sans aucun message. La librairie n'expose pas de
   * recalcul, le redémarrage du flux est le seul recours.
   *
   * Seule la **largeur** du conteneur est surveillée. Sur Android, l'ouverture du
   * clavier virtuel réduit la hauteur de la fenêtre sans toucher à la largeur:
   * réagir à la hauteur redémarrerait la caméra chaque fois que l'utilisateur
   * touche le champ de saisie, qui est juste au-dessus de l'aperçu.
   */
  React.useEffect(() => {
    const container = document.getElementById(elementId);
    if (!container || typeof ResizeObserver === "undefined") return undefined;

    let lastWidth = container.clientWidth;
    let timer = null;

    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      if (width === 0 || width === lastWidth) return;
      lastWidth = width;

      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        console.info(`[CameraScanner] Largeur de l'aperçu modifiée (${width}px), redémarrage du flux.`);
        // La hauteur imposée a été calculée sur l'orientation précédente et est
        // injectée en `!important`: il faut la retirer avant de remesurer.
        setForcedVideoHeight(null);
        setRestartToken((token) => token + 1);
      }, RESIZE_SETTLE_MS);
    });

    observer.observe(container);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [elementId]);

  const handleZoomChange = (_event, value) => {
    setZoom(value);
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      scanner.getRunningTrackCameraCapabilities().zoomFeature().apply(value);
    } catch (err) {
      console.warn("Failed to apply zoom:", err);
    }
  };

  /**
   * Bascule entre autofocus continu et mise au point manuelle. La mise au point
   * manuelle est l'échappatoire quand l'autofocus cherche sans se fixer: une
   * fois la netteté réglée, l'objectif ne bouge plus.
   */
  const handleManualFocusToggle = () => {
    const scanner = scannerRef.current;
    if (!scanner || !focusRange) return;

    const next = !manualFocus;
    const constraint = next
      ? { focusMode: FOCUS_MANUAL, focusDistance: focusDistance === null ? focusRange.min : focusDistance }
      : { focusMode: FOCUS_CONTINUOUS };

    applyFocusConstraint(scanner, constraint).then(() => setManualFocus(next));
  };

  const handleFocusDistanceChange = (_event, value) => {
    setFocusDistance(value);
    const scanner = scannerRef.current;
    if (!scanner) return;
    applyFocusConstraint(scanner, { focusMode: FOCUS_MANUAL, focusDistance: value });
  };

  const handleTorchToggle = () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    const next = !torchOn;
    try {
      scanner
        .getRunningTrackCameraCapabilities()
        .torchFeature()
        .apply(next)
        .then(() => setTorchOn(next))
        .catch((err) => console.warn("Failed to toggle torch:", err));
    } catch (err) {
      console.warn("Failed to toggle torch:", err);
    }
  };

  return (
    <div className={classes.root}>
      {startupError ? (
        <div className={classes.status}>
          <div className={classes.errorMsg}>{startupError}</div>
        </div>
      ) : (
        <>
          {forcedVideoHeight !== null && (
            // Portée limitée à cette instance de scanner. La seule valeur
            // interpolée est un entier calculé plus haut.
            <style>{`#${elementId} > video { height: ${forcedVideoHeight}px !important; }`}</style>
          )}
          <div className={classes.viewfinderWrapper}>
            <div id={elementId} className={classes.viewfinder} />
            {isStarting && (
              <div className={classes.startingOverlay}>
                <CircularProgress size={20} color="inherit" />
                <span>{I18n.get("qrScanStartingCamera")}</span>
              </div>
            )}
          </div>
          {!isStarting && (
            <>
              <div className={classes.hint}>{I18n.get("qrScanHint")}</div>
              {(zoomRange || torchSupported || focusRange) && (
                <div className={classes.controls}>
                  {zoomRange && (
                    <>
                      <ZoomIn fontSize="small" htmlColor="#00000099" />
                      <Slider
                        className={classes.zoomSlider}
                        value={zoom === null ? zoomRange.min : zoom}
                        min={zoomRange.min}
                        max={zoomRange.max}
                        step={zoomRange.step}
                        onChange={handleZoomChange}
                        aria-label={I18n.get("qrScanZoom")}
                      />
                    </>
                  )}
                  {focusRange && (
                    <IconButton
                      size="small"
                      onClick={handleManualFocusToggle}
                      aria-label={I18n.get("qrScanFocusManual")}
                      aria-pressed={manualFocus}
                    >
                      {manualFocus ? (
                        <CenterFocusStrong color="primary" />
                      ) : (
                        <CenterFocusWeak htmlColor="#00000099" />
                      )}
                    </IconButton>
                  )}
                  {torchSupported && (
                    <IconButton
                      size="small"
                      onClick={handleTorchToggle}
                      aria-label={I18n.get("qrScanTorch")}
                      aria-pressed={torchOn}
                    >
                      {torchOn ? <FlashOn color="primary" /> : <FlashOff htmlColor="#00000099" />}
                    </IconButton>
                  )}
                </div>
              )}
              {focusRange && manualFocus && (
                <div className={classes.controls}>
                  <span className={classes.controlLabel}>{I18n.get("qrScanFocusDistance")}</span>
                  <Slider
                    className={classes.zoomSlider}
                    value={focusDistance === null ? focusRange.min : focusDistance}
                    min={focusRange.min}
                    max={focusRange.max}
                    step={focusRange.step}
                    onChange={handleFocusDistanceChange}
                    aria-label={I18n.get("qrScanFocusDistance")}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(CameraScanner);
